import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Edit, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  EventsPageSettings,
  deleteEvent,
  fetchAdminEvents,
  fetchEventsPageSettings,
  upsertEventsPageSettings,
} from "@/services/eventService";
import { useLanguage } from "@/context/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const EventsManagement = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [isUploadingHeroImage, setIsUploadingHeroImage] = useState(false);

  const [heroSettings, setHeroSettings] = useState<EventsPageSettings>({
    heroEyebrowEn: "Experience Meubles Karim",
    heroEyebrowFr: "Experience Meubles Karim",
    heroTitleEn: "Our Signature Events",
    heroTitleFr: "Nos Evenements Signature",
    heroSubtitleEn:
      "Discover our standout moments: launches, private showcases, and immersive design experiences.",
    heroSubtitleFr:
      "Decouvrez nos moments d'exception: lancements, showcases prives et experiences immersives.",
    heroBackgroundImage: "",
  });

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["admin", "events"],
    queryFn: fetchAdminEvents,
  });
  const { data: loadedSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ["admin", "events", "page-settings"],
    queryFn: fetchEventsPageSettings,
  });
  useEffect(() => {
    if (loadedSettings) {
      setHeroSettings(loadedSettings);
    }
  }, [loadedSettings]);

  const deleteMutation = useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      queryClient.invalidateQueries({ queryKey: ["events", "published"] });
      toast({
        title: "Event deleted",
        description: "The event has been removed successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete the event.",
        variant: "destructive",
      });
    },
  });

  const saveSettingsMutation = useMutation({
    mutationFn: upsertEventsPageSettings,
    onSuccess: (savedSettings) => {
      setHeroSettings(savedSettings);
      queryClient.invalidateQueries({ queryKey: ["events", "page-settings"] });
      toast({
        title: "Hero settings updated",
        description: "Events page hero content has been saved.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save hero settings.",
        variant: "destructive",
      });
    },
  });

  const uploadHeroImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingHeroImage(true);
      const ext = file.name.split(".").pop();
      const filePath = `events/page-hero-${Date.now()}.${ext}`;

      const { error } = await supabase.storage.from("events").upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
      if (error) throw error;

      const { data } = supabase.storage.from("events").getPublicUrl(filePath);
      setHeroSettings((current) => ({ ...current, heroBackgroundImage: data.publicUrl }));
      toast({
        title: "Image uploaded",
        description: "Hero background image is ready. Save settings to publish it.",
      });
    } catch (error: any) {
      toast({
        title: "Upload error",
        description: error.message || "Failed to upload hero background image.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingHeroImage(false);
    }
  };

  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return events;
    return events.filter((event) => {
      const title = event.title[language]?.toLowerCase() ?? "";
      const slug = event.slug.toLowerCase();
      return title.includes(query) || slug.includes(query);
    });
  }, [events, search, language]);

  return (
    <div>
      <Helmet>
        <title>Events Management | Admin Dashboard</title>
      </Helmet>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-sans font-semibold">Events Management</h1>
        <Button asChild>
          <Link to="/admin/events/new">
            <Plus size={16} className="mr-1" /> Add Event
          </Link>
        </Button>
      </div>

      <div className="bg-white rounded-md shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-sans font-semibold">Events Hero Settings</h2>
          <Button
            onClick={() => saveSettingsMutation.mutate(heroSettings)}
            disabled={saveSettingsMutation.isPending || isLoadingSettings}
          >
            {saveSettingsMutation.isPending ? "Saving..." : "Save Hero Settings"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Eyebrow (EN)</Label>
            <Input
              value={heroSettings.heroEyebrowEn}
              onChange={(event) =>
                setHeroSettings((current) => ({ ...current, heroEyebrowEn: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Eyebrow (FR)</Label>
            <Input
              value={heroSettings.heroEyebrowFr}
              onChange={(event) =>
                setHeroSettings((current) => ({ ...current, heroEyebrowFr: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Title (EN)</Label>
            <Input
              value={heroSettings.heroTitleEn}
              onChange={(event) =>
                setHeroSettings((current) => ({ ...current, heroTitleEn: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Title (FR)</Label>
            <Input
              value={heroSettings.heroTitleFr}
              onChange={(event) =>
                setHeroSettings((current) => ({ ...current, heroTitleFr: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Subtitle (EN)</Label>
            <Textarea
              rows={3}
              value={heroSettings.heroSubtitleEn}
              onChange={(event) =>
                setHeroSettings((current) => ({ ...current, heroSubtitleEn: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Subtitle (FR)</Label>
            <Textarea
              rows={3}
              value={heroSettings.heroSubtitleFr}
              onChange={(event) =>
                setHeroSettings((current) => ({ ...current, heroSubtitleFr: event.target.value }))
              }
            />
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <Label>Hero Background Image URL</Label>
          <Input
            value={heroSettings.heroBackgroundImage}
            onChange={(event) =>
              setHeroSettings((current) => ({ ...current, heroBackgroundImage: event.target.value }))
            }
            placeholder="https://..."
          />
          <div className="flex items-center gap-3">
            <Input type="file" accept="image/*" onChange={uploadHeroImage} disabled={isUploadingHeroImage} />
            <span className="text-xs text-muted-foreground">
              {isUploadingHeroImage ? "Uploading..." : "Upload image to events storage"}
            </span>
          </div>
          {heroSettings.heroBackgroundImage && (
            <img
              src={heroSettings.heroBackgroundImage}
              alt="Events hero preview"
              className="w-full max-w-md h-36 object-cover rounded border"
            />
          )}
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm p-6">
        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search events..."
            className="pl-8"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">Loading events...</div>
        ) : filteredEvents.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">No events found.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-3">
                        <img
                          src={event.coverImage || event.images[0]?.url || "/placeholder.svg"}
                          alt={event.title[language]}
                          className="h-10 w-10 object-cover rounded"
                        />
                        <span className="line-clamp-1">{event.title[language]}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-muted-foreground" />
                        {new Date(event.date).toLocaleDateString(language === "fr" ? "fr-FR" : "en-GB")}
                      </span>
                    </TableCell>
                    <TableCell>{event.slug}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          event.isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {event.isPublished ? "Published" : "Draft"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/events/edit/${event.id}`}>
                            <Edit size={16} />
                          </Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone and will permanently remove this event.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => deleteMutation.mutate(event.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsManagement;
